/**
 * Fil conducteur du bureau de vote : l'état de chaque étape du parcours.
 *
 * Le professeur ne doit pas deviner où il en est, ni pourquoi une action est
 * impossible. Ce module est volontairement **pur** : il ne lit que l'état
 * projeté, le calcul et un petit diagnostic. Il est donc testable seul, et
 * l'interface ne fait que l'afficher.
 */

import { PHASE_LABEL, type Computation, type ElectionState, type Phase } from '../core/index.js';

/** Ce que l'interface sait et que le noyau ignore (stockage, réseau). */
export interface DiagnosticParcours {
  readonly eleves: number;
  readonly codes: number;
  readonly bulletins: number;
  readonly derniereSauvegarde: number | null;
  readonly enLigne: boolean;
}

export type EtatEtape = 'fait' | 'encours' | 'a_faire' | 'bloque' | 'probleme';

export interface EtapeParcours {
  /** 1 à 6 : le numéro affiché dans la barre de progression. */
  readonly cle: number;
  readonly titre: string;
  /** Ligne courte affichée sous le titre : « 12 élèves », « 8 codes »… */
  resume: string;
  etat: EtatEtape;
  /** Pourquoi l'étape est bloquée, ou ce qui coince. `null` si tout va bien. */
  blocage: string | null;
  /** Étape à corriger pour débloquer celle-ci, s'il y en a une. */
  debloque: number | null;
}

/** Phases postérieures au dépouillement : le scrutin est derrière nous. */
const APRES_SCRUTIN: readonly Phase[] = [
  'DEPOUILLEMENT1',
  'TOUR2',
  'DEPOUILLEMENT2',
  'DEPARTAGE',
  'ACCEPTATION',
  'DESIGNATION',
  'PROCLAMATION',
];

export function etapesParcours(
  state: ElectionState,
  computation: Computation,
  diagnostic: DiagnosticParcours,
): EtapeParcours[] {
  const eleves = diagnostic.eleves;
  const codes = diagnostic.codes;
  const items = computation.items;
  const binomes = items.filter((item) => item.kind === 'binome').length;
  const phase = state.phase;
  const etapeVotePassee = APRES_SCRUTIN.includes(phase);
  const proclame = phase === 'PROCLAMATION';
  const sauvegarde = diagnostic.derniereSauvegarde;

  // 1 — liste de classe
  const etape1: EtapeParcours = {
    cle: 1,
    titre: 'Liste de classe',
    resume: eleves > 0 ? `${eleves} élève${eleves > 1 ? 's' : ''}` : 'Aucune liste',
    etat: eleves > 0 ? 'fait' : 'encours',
    blocage: eleves > 0 ? null : 'Collez ou importez la liste : tout le reste en dépend.',
    debloque: null,
  };

  // 2 — codes de vote
  const etape2: EtapeParcours = {
    cle: 2,
    titre: 'Codes de vote',
    resume: codes > 0 ? `${codes} code${codes > 1 ? 's' : ''} généré${codes > 1 ? 's' : ''}` : 'À générer',
    etat: codes > 0 ? 'fait' : 'a_faire',
    blocage: null,
    debloque: null,
  };
  if (codes === 0 && eleves === 0) {
    etape2.etat = 'bloque';
    etape2.blocage = 'Importez d’abord la liste de classe.';
    etape2.debloque = 1;
  }

  // 3 — candidatures
  const candidaturesOuvertes = phase === 'BROUILLON' || phase === 'CANDIDATURES';
  const etape3: EtapeParcours = {
    cle: 3,
    titre: 'Candidatures',
    resume:
      items.length > 0
        ? `${items.length} votable${items.length > 1 ? 's' : ''}${binomes > 0 ? ` · ${binomes} binôme${binomes > 1 ? 's' : ''}` : ''}`
        : 'Aucune candidature',
    etat: candidaturesOuvertes && binomes === 0 ? 'encours' : 'fait',
    blocage: null,
    debloque: null,
  };
  if (eleves === 0) {
    etape3.etat = 'bloque';
    etape3.blocage = 'Importez d’abord la liste de classe.';
    etape3.debloque = 1;
  } else if (!candidaturesOuvertes && items.length < 2) {
    etape3.etat = 'probleme';
    etape3.blocage = 'Il faut au moins deux items votables pour ouvrir le scrutin.';
    etape3.debloque = 3;
  }

  // 4 — scrutin
  const etape4: EtapeParcours = {
    cle: 4,
    titre: 'Scrutin',
    resume: `${PHASE_LABEL[phase]} · ${diagnostic.bulletins} bulletin${diagnostic.bulletins > 1 ? 's' : ''}${diagnostic.enLigne ? ' · en ligne' : ''}`,
    etat: etapeVotePassee ? 'fait' : phase === 'TOUR1' || phase === 'TOUR2' ? 'encours' : 'a_faire',
    blocage: null,
    debloque: null,
  };
  if (codes === 0) {
    etape4.etat = 'bloque';
    etape4.blocage = 'Générez les codes de vote avant d’ouvrir le scrutin.';
    etape4.debloque = 2;
  } else if (items.length < 2) {
    etape4.etat = 'bloque';
    etape4.blocage = 'Il faut au moins deux items votables.';
    etape4.debloque = 3;
  } else if (phase === 'BROUILLON' || phase === 'CANDIDATURES' || phase === 'VALIDATION') {
    etape4.etat = 'a_faire';
  }

  // 5 — résultats
  const etape5: EtapeParcours = {
    cle: 5,
    titre: 'Résultats',
    resume: proclame
      ? `${computation.siegesCalcules.length} siège${computation.siegesCalcules.length > 1 ? 's' : ''} pourvu${computation.siegesCalcules.length > 1 ? 's' : ''}`
      : etapeVotePassee
        ? 'À proclamer'
        : 'En attente du scrutin',
    etat: proclame ? 'fait' : etapeVotePassee ? 'encours' : 'a_faire',
    blocage: null,
    debloque: null,
  };
  if (!etapeVotePassee) {
    etape5.etat = 'bloque';
    etape5.blocage = 'Le dépouillement s’ouvre après la clôture du scrutin.';
    etape5.debloque = 4;
  } else if (computation.ambigu !== null) {
    etape5.etat = 'probleme';
    etape5.blocage = `Égalité au tour ${computation.ambigu.round} : un départage manuel est obligatoire.`;
    etape5.debloque = 5;
  } else if (computation.designationsAttendues.length > 0) {
    etape5.etat = 'probleme';
    etape5.blocage = `${computation.designationsAttendues.length} suppléant(s) restent à désigner.`;
    etape5.debloque = 5;
  }

  // 6 — sauvegarde & procès-verbal
  const etape6: EtapeParcours = {
    cle: 6,
    titre: 'Sauvegarde & PV',
    resume: sauvegarde === null ? 'Aucune sauvegarde exportée' : 'Sauvegarde exportée',
    etat: sauvegarde === null ? 'probleme' : 'fait',
    blocage: null,
    debloque: null,
  };
  if (sauvegarde === null) {
    etape6.blocage =
      eleves > 0 || codes > 0
        ? 'Exportez une sauvegarde : c’est le seul moyen de reprendre sur un autre ordinateur.'
        : 'La sauvegarde se fera une fois la liste et les codes prêts.';
    etape6.debloque = 6;
  } else if (!proclame) {
    etape6.etat = 'encours';
    etape6.resume = 'Sauvegarde faite · PV après proclamation';
  }

  return [etape1, etape2, etape3, etape4, etape5, etape6];
}

/** Étape à ouvrir par défaut : la première qui n'est pas terminée. */
export function etapeRecommandee(etapes: readonly EtapeParcours[]): number {
  return etapes.find((etape) => etape.etat !== 'fait')?.cle ?? 6;
}
